<script lang="ts">
	import type {
		BoundaryCoordinates,
		Coordinates,
		MapDetails,
		MapEvent,
		RechargePoint,
		RouteWithGeometry,
	} from '$lib/components/map/types';
	import type { MainPoiCategory, PoiData } from '$lib/components/pois/types';
	import type { SearchAddressResult } from '$lib/components/search/address/types';
	import type { TrafficInformationEvent, TrafficInformationSummary } from '$lib/components/traffic/types';
	import type { Zoi } from '$lib/components/zoi/types';
	import type { MobileTopButton } from '$routes/(map)/types';
	import type { LngLatBounds, MapMouseEvent, MapSourceDataEvent } from 'maplibre-gl';
	import type { ClassValue } from 'svelte/elements';
	import type { Writable } from 'svelte/store';
	import { page } from '$app/state';
	import { env } from '$env/dynamic/public';
	import { type CoordinatesInput, ReverseGeocodeStore } from '$houdini';
	import MapAdBanner from '$lib/components/advertising/MapAdBanner.svelte';
	import PartnerBoxMaps from '$lib/components/advertising/partners/boxmap/PartnerBoxMaps.svelte';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import CorridorPolygon from '$lib/components/corridor/CorridorPolygon.svelte';
	import Header from '$lib/components/header/Header.svelte';
	import { MAPLIBRE_RTL_TEXT_PLUGIN_URL, MAX_FIT_ZOOM } from '$lib/components/map/constants';
	import ItineraryMarkers from '$lib/components/map/itinerary/ItineraryMarkers.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import { areBoundsContained, getBoundsOfAnArea, getViewPortBoundsFromCenter } from '$lib/components/map/map-utils';
	import MapTools from '$lib/components/map/mapTools/MapTools.svelte';
	import LocationMarker from '$lib/components/map/markers/LocationMarker.svelte';
	import PoiMarker from '$lib/components/map/markers/PoiMarker.svelte';
	import RechargePointMarker from '$lib/components/map/markers/RechargePointMarker.svelte';
	import RouteMarker from '$lib/components/map/markers/RouteMarker.svelte';
	import TrafficMarker from '$lib/components/map/markers/TrafficMarker.svelte';
	import WaypointMarker from '$lib/components/map/markers/WaypointMarker.svelte';
	import RefreshPoisButton from '$lib/components/map/refresh-pois/RefreshPoisButton.svelte';
	import RoutePolylines from '$lib/components/map/routePolylines/RoutePolylines.svelte';
	import RouteTrafficPolylines from '$lib/components/map/routePolylines/RouteTrafficPolylines.svelte';
	import ZoiPolygon from '$lib/components/zoi/ZoiPolygon.svelte';
	import { POIS_HRT } from '$lib/constants/poilist';
	import { isMobile } from '$lib/services/mediaQuery/screenSize';
	import { createSearchHistoryStore } from '$lib/services/store/searchHistory';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { trackDisableFullscreen } from '$lib/services/tracking/googleAnalytics/fullscreen';
	import { getPoiMapTrackingEvent } from '$lib/services/tracking/googleAnalytics/pois';
	import { buildItineraryTrackingData } from '$lib/services/tracking/googleAnalytics/tracking';
	import { buildZoiSelectionTrackingData } from '$lib/services/tracking/googleAnalytics/zoi';
	import {
		isCorridorPageContext,
		isHomePage,
		isMapPageContext,
		isRouteDetailsPageContext,
		isRouteResultsPage,
		isRouteWithoutPOIs,
		isZoiPageContext,
	} from '$lib/services/urls/routeIds';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import { gotoZoi } from '$lib/utils/gotoZoi';
	import * as m from '$paraglide/messages';
	import maplibregl from 'maplibre-gl';
	import { watch } from 'runed';
	import { onDestroy } from 'svelte';
	import { AttributionControl, MapLibre, ScaleControl } from 'svelte-maplibre';
	import { preventDefault } from 'svelte/legacy';
	import { debounce } from 'throttle-debounce';
	import { getSelectedAddress, setSelectedAddress } from './selectedAddress.svelte';

	const searchParams = getSearchParamsStore();
	const mapLayoutContext = getMapLayoutContext();
	const searchHistoryStore = createSearchHistoryStore();

	const {
		mapController,
		mapLayerName,
		mapStyleUrl,
		itineraryStore,
		isLowEmissionZoneLayerVisible,
		isTrafficInfoVisible,
		isRefreshButtonVisible,
		tripOptionsStore,
		isTrafficInfoPageSwitchOn,
		isOneRoutePolylinesVisible,
		trafficEventSummary,
		isMapLoaded,
		drawerScrollPosition,
		isPinMarkerOnMapWithSummary,
		clickedPosition,
		isMapFullscreen,
		poiMarkerWithDetailsEnabled,
		partnerBoxMapsAreReduced,
		isSummaryOpen,
		poiCoordinates,
	} = mapLayoutContext;

	const {
		LngLatBounds: LngLatBoundsClass,
		LngLat,
		setRTLTextPlugin,
		getRTLTextPluginStatus,
	} = maplibregl;

	// limit zoom levels to usable values
	const MAX_ZOOM = 22;
	const MIN_ZOOM = 1;
	// 393 is 50 + 343 (bottomsheet height on route results page)
	const MAP_PADDING_MOBILE = { top: 50, right: 60, bottom: 393, left: 50 };
	const MAP_PADDING_DESKTOP = { top: 100, right: 100, bottom: 150, left: 100 };
	const MOBILE_CENTER_AREA_PADDING_RATIO = {
		left: 0.25,
		right: 0.25,
		bottom: 0.33,
		top: 0.33,
	};
	const DESKTOP_CENTER_AREA_PADDING_RATIO = {
		left: 0.1,
		right: 0.1,
		bottom: 0.2,
		top: 0.1,
	};
	const MIN_LOW_LEVEL_ZOOM = 16;
	const MAX_LOW_LEVEL_ZOOM = 22;

	let reverseGeocodeStore: ReverseGeocodeStore;

	interface Props {
		mapBounds?: BoundaryCoordinates;
		// TODO: convert to type MainPoiCategory[] when applications/web/src/routes/(map)/+layout.svelte is using runes
		selectedPoiCategories: Writable<MainPoiCategory[]>;
		poiData: PoiData[];
		selectedPoiData?: PoiData;
		selectedAddressCenter?: Coordinates;
		mobileTopButtons?: MobileTopButton[];
		routes: RouteWithGeometry[];
		zois: Zoi[];
		corridor?: number[][][];
		isTrafficPage: boolean;
		trafficEvents: TrafficInformationEvent[];
		shouldDisplaySummary: boolean;
		enableToolsPartnerPoiCategory?: boolean;
		mapWidth?: number;
		class?: ClassValue;
		onMapBoundsChange?: (bounds: LngLatBounds) => void;
		onSearchBoundsChange?: (mapDetails: MapDetails & { selectedPois?: MainPoiCategory[] }) => void;
		onSelectedPoiChange?: (poiData: PoiData | null) => void;
		onBackHome?: () => void;
		onBackOverview?: () => void;
		onBackToRouteResults?: () => void;
		onBackOnTheWay?: () => void;
		onPoiCategoriesChange?: (categories: MainPoiCategory[]) => void;
	}

	let {
		mapBounds,
		selectedPoiCategories,
		poiData,
		selectedPoiData,
		selectedAddressCenter = $bindable(),
		mobileTopButtons,
		routes = [],
		zois = [],
		corridor,
		isTrafficPage,
		trafficEvents,
		shouldDisplaySummary,
		enableToolsPartnerPoiCategory = false,
		mapWidth,
		class: classes,
		onMapBoundsChange,
		onSearchBoundsChange,
		onSelectedPoiChange,
		onBackHome,
		onBackOverview,
		onBackToRouteResults,
		onBackOnTheWay,
		onPoiCategoriesChange,
	}: Props = $props();

	const SINGLE_CLICK_DELAY = 300;

	const { itinerary } = $derived(itineraryStore);
	const { itineraryResultsStore } = $derived(page.data);

	let clickEvents: MapEvent[] = [];
	const selectedRoute = $derived($searchParams.selectedRoute ?? 0);
	const selectedRouteRechargePoints: RechargePoint[] | undefined = $derived(routes?.[selectedRoute]?.evData?.rechargePoints);

	let loadedMap: maplibregl.Map | undefined = $state();

	const initialMapBounds = mapBounds;

	let resizeObserver: ResizeObserver | null = $state(null);
	const { PUBLIC_FEATURE_AUTO_RELOAD_POIS_ENABLED } = env;
	const enableAutoReloadPOIs = PUBLIC_FEATURE_AUTO_RELOAD_POIS_ENABLED === 'true';
	let autoReloadPOIs = $state(false);

	let isMapFlying = $state(false);
	let shouldReloadPoisOnGeolocationGranted = $state(false);

	let currentZoom: number | undefined = $state();
	const shouldLoadPoisOnHome = $derived(isHomePage(page.url) && !$selectedPoiCategories.length);

	function centerMapOnCoordinates(coordinates: Coordinates) {
		if (loadedMap) {
			isMapFlying = true;
			loadedMap.flyTo({ center: coordinates, zoom: MAX_FIT_ZOOM });
		}
	}

	function centerMapAroundMySelection(coordinates: Coordinates) {
		if (loadedMap) {
			const bounds = loadedMap.getBounds();
			const boundsOfTheArea = getBoundsOfAnArea(
				bounds,
				$isMobile ? MOBILE_CENTER_AREA_PADDING_RATIO : DESKTOP_CENTER_AREA_PADDING_RATIO,
			);

			const isNotInBounds = boundsOfTheArea && !boundsOfTheArea.contains(coordinates);
			if (isNotInBounds) {
				loadedMap.easeTo({ center: coordinates });
			}
		}
	}

	function handleClickManagementDelay(event?: MapMouseEvent) {
		setTimeout(() => {
			clicksManagement(event);
		}, SINGLE_CLICK_DELAY);
	}

	function handlePoiClick(poi: PoiData) {
		track(getPoiMapTrackingEvent(poi));
		onSelectedPoiChange?.(poi);
		$clickedPosition = undefined;
		centerMapAroundMySelection(poi.coordinates);
	}

	function handleTrafficEventClick(trafficInfo: TrafficInformationSummary) {
		$trafficEventSummary = trafficInfo;
		$clickedPosition = undefined;
		centerMapAroundMySelection(trafficInfo.coordinates);
	}

	function onMapClick(event: MapMouseEvent) {
		clickEvents.push('mapClick');
		handleClickManagementDelay(event);
	}

	function dispatchMapBoundsChange() {
		handleAutoReloadPOIs();
		if (loadedMap) {
			onMapBoundsChange?.(loadedMap.getBounds());
		}
	}

	function handleAutoReloadPOIs() {
		if (enableAutoReloadPOIs && loadedMap && !isRouteWithoutPOIs(page.route)) {
			const zoomLevel = loadedMap.getZoom() || 0;
			const bounds = loadedMap.getBounds();

			if (zoomLevel >= MIN_LOW_LEVEL_ZOOM && zoomLevel <= MAX_LOW_LEVEL_ZOOM) {
				autoReloadPOIs = true;
				onSearchBoundsChange?.({
					mapBounds: bounds.toArray() as BoundaryCoordinates,
					userAction: null,
				});
				return;
			}
		}

		autoReloadPOIs = false;
	}

	function fitCoordinates(coordinates?: number[][]): undefined {
		const bounds = new LngLatBoundsClass();

		if (!coordinates || !loadedMap) {
			return;
		}

		coordinates.forEach((coordinate) => {
			bounds.extend([coordinate[0], coordinate[1]]);
		});

		isMapFlying = true;
		loadedMap.fitBounds(bounds, {
			padding: $isMobile ? MAP_PADDING_MOBILE : MAP_PADDING_DESKTOP,
		});
	}

	function fitCircle(center: Coordinates, radius: number) {
		if (!center || !radius || !loadedMap) {
			return;
		}

		const radiusInMeters = radius * 1000;

		const bounds = getBoundsFromRadius(center, radiusInMeters);
		isMapFlying = true;
		loadedMap.fitBounds(bounds, {
			padding: $isMobile ? MAP_PADDING_MOBILE : MAP_PADDING_DESKTOP,
		});
	}

	function getBoundsFromRadius(center: Coordinates, radius: number) {
		const EARTH_RADIUS = 6371000;
		const lat = center.lat * Math.PI / 180;
		const dLat = radius / EARTH_RADIUS;
		const dLng = radius / (EARTH_RADIUS * Math.cos(lat));

		const latMin = center.lat - dLat * (180 / Math.PI);
		const latMax = center.lat + dLat * (180 / Math.PI);
		const lngMin = center.lng - dLng * (180 / Math.PI);
		const lngMax = center.lng + dLng * (180 / Math.PI);

		return new maplibregl.LngLatBounds(
			[lngMin, latMin],
			[lngMax, latMax],
		);
	}

	function handleSelectedAddressChange({
		mapLocation: { location, viewPort },
		formattedName,
		entityType,
		address: { city, country, region, zipCode },
		userAction,
	}: SearchAddressResult) {
		if (loadedMap) {
			const addressViewPortBounds = new LngLatBoundsClass(
				[viewPort.southwest.lng, viewPort.southwest.lat],
				[viewPort.northeast.lng, viewPort.northeast.lat],
			);

			const centerBounds = getViewPortBoundsFromCenter({
				currentZoom: loadedMap.getZoom(),
				desiredZoom: MAX_FIT_ZOOM,
				currentBounds: loadedMap.getBounds(),
				desiredCenter: location,
			});

			const areViewPortBoundsContained = areBoundsContained(addressViewPortBounds, centerBounds);
			const newBounds = areViewPortBoundsContained ? centerBounds : addressViewPortBounds;

			isMapFlying = true;
			loadedMap.fitBounds(newBounds);

			onSearchBoundsChange?.({
				center: location,
				mapBounds: newBounds.toArray() as BoundaryCoordinates,
				formattedAddress: {
					address: formattedName,
					entityType: entityType ?? undefined,
					countryCode: country?.code ?? undefined,
					region: region ?? undefined,
					zipCode: zipCode ?? undefined,
					coordinates: location,
					boundsSync: true,
					city: city ?? undefined,
				},
				userAction,
				...(shouldLoadPoisOnHome ? { selectedPois: POIS_HRT } : {}),

			});
		}
	}

	function onMapMoveEnd() {
		if (!loadedMap) {
			return;
		}

		currentZoom = loadedMap.getZoom();
		isMapFlying = false;

		if (
			shouldReloadPoisOnGeolocationGranted
			&& currentZoom
			&& currentZoom >= 15
			&& isHomePage(page.url)
		) {
			onSearchBoundsChange?.({
				mapBounds: loadedMap.getBounds().toArray() as BoundaryCoordinates,
				userAction: 'click',
				selectedPois: $selectedPoiCategories.length === 0 ? POIS_HRT : $selectedPoiCategories,
			});
			shouldReloadPoisOnGeolocationGranted = false;
		}

		// TODO : check the need of the second event
		dispatchMapBoundsChange();
	}

	onDestroy(() => {
		if (loadedMap) {
			loadedMap.off('click', onMapClick);
			loadedMap.off('moveend', onMapMoveEnd);
			loadedMap.off('sourcedata', handleSourceData);
		}

		if (resizeObserver) {
			resizeObserver.disconnect();
		}
	});

	function center(coordinates: Coordinates, zoom?: number) {
		if (loadedMap) {
			isMapFlying = true;
			loadedMap.flyTo({ center: coordinates, zoom });
		}
	}

	/**
	 * Resize the map using the top left position as an anchor.
	 *
	 * By default, the resize function of maplibre will resize the map based
	 * on the center. This means when the map grows, the whole map is moved so the center
	 * remains the same.
	 * The idea here is to have the same behavior but using the top left edge as the center
	 * so the map does not flicker.
	 */
	function resize() {
		if (window.matchMedia('(max-width: 960px)').matches && $isMapFullscreen) {
			$isMapFullscreen = false;
		}

		// If the map is flying, a resize will cancel the flying animation thus causiing some issues
		if (loadedMap && !isMapFlying) {
			const nw = loadedMap.getBounds().getNorthWest();
			loadedMap.resize();
			const newNw = loadedMap.getBounds().getNorthWest();
			const currentCenter = loadedMap.getCenter();
			const shiftVector = { x: newNw.lng - nw.lng, y: newNw.lat - nw.lat };
			const newCenter = new LngLat(
				currentCenter.lng - shiftVector.x,
				currentCenter.lat - shiftVector.y,
			);
			loadedMap.setCenter(newCenter);
		}
	}

	function handlePolylineClick(index: number) {
		$isOneRoutePolylinesVisible = true;
		clickEvents.push('polylineClick');
		const selectedRoute = index;
		if (routes) {
			track({
				action: 'itinerary_polyline',
				payload: {
					action: 'selection',
					...buildItineraryTrackingData(page),
				},
			});
		}

		$searchParams = { ...$searchParams, selectedRoute, itinerary: $itinerary };
	}

	function handleRouteIndexChange(routeIndex: number) {
		$isOneRoutePolylinesVisible = true;
		const newSelectedRouteIndex = routeIndex;

		track({
			action: 'itinerary_tooltip',
			payload: {
				action: 'selection',
				...buildItineraryTrackingData(page),
			},
		});

		const itineraryParams = {
			selectedRoute: newSelectedRouteIndex,
			itinerary: $itinerary,
		};

		$searchParams = { ...$searchParams, ...itineraryParams };
	}

	function lookupAddress(lookupPos: Coordinates | undefined) {
		if (!lookupPos) {
			return;
		}

		const position: CoordinatesInput = lookupPos;
		if (!reverseGeocodeStore) {
			reverseGeocodeStore = new ReverseGeocodeStore();
		}

		return reverseGeocodeStore
			.fetch({ variables: { position }, blocking: true })
			.then((result) => {
				if (result.data?.reverseGeocode.__typename === 'ReverseGeocodeSuccessResult' && loadedMap) {
					selectedAddressCenter = $clickedPosition;
					$searchParams.center = $clickedPosition;
					$searchParams.poiCategories = shouldLoadPoisOnHome ? POIS_HRT : $selectedPoiCategories;

					if (shouldLoadPoisOnHome) {
						$searchParams.bounds = loadedMap.getBounds().toArray() as BoundaryCoordinates;
					}

					const selectedAddress = result.data?.reverseGeocode.address;

					const bound = getViewPortBoundsFromCenter({
						currentBounds: loadedMap.getBounds(),
						currentZoom: loadedMap.getZoom(),
						desiredCenter: result.data.reverseGeocode.mapLocation.location,
						desiredZoom: loadedMap.getZoom(),
					});

					searchHistoryStore?.add({
						formattedName: selectedAddress?.formattedAddress || '',
						contextualizedName: null,
						entityType: 'CITY',
						score: 0,
						address: {
							city: selectedAddress?.city || '',
							department: null,
							region: null,
							country: {
								code: selectedAddress?.country?.code || '',
							},
							zipCode: selectedAddress?.zipCode || '',
						},
						mapLocation: {
							location: result.data.reverseGeocode.mapLocation.location,
							viewPort: {
								northeast: bound._ne,
								southwest: bound._sw,
							},
						},
					});

					$searchParams.selectedAddress = {
						address: selectedAddress?.formattedAddress || '',
						entityType: 'CITY',
						coordinates: result.data.reverseGeocode.mapLocation.location,
						boundsSync: false,
					};

					if (!$searchParams.itinerary?.length) {
						$searchParams.itinerary = $itinerary;
					}
				}
			})
			.catch(() => {});
	}

	function checkEventsPresence(eventsToCheck: MapEvent[]) {
		return eventsToCheck.every(event => clickEvents.includes(event));
	}

	function clicksManagement(event: MapMouseEvent | undefined) {
		const singleClick = clickEvents.length === 1 && checkEventsPresence(['mapClick']);
		const singleTap = clickEvents.length === 2 && checkEventsPresence(['touchend', 'mapClick']);

		if (singleClick || singleTap) {
			if (selectedPoiData) {
				onSelectedPoiChange?.(null);
			} else if ($clickedPosition) {
				$clickedPosition = undefined;
				selectedAddressCenter = undefined;
				setSelectedAddress(null);
				$searchParams.selectedAddress = undefined;
				$searchParams.center = undefined;
				$isPinMarkerOnMapWithSummary = false;
				$isSummaryOpen = false;
			} else if ($trafficEventSummary) {
				$trafficEventSummary = null;
			} else {
				// POIMarker clicks don't reach here so we load the address
				$clickedPosition = event?.lngLat || undefined;
				$isPinMarkerOnMapWithSummary = !$isPinMarkerOnMapWithSummary;

				lookupAddress($clickedPosition);
			}
		}
		// To be able to handle single-click/tap, we have to track recent events in this array.
		// A delay timer is started on the first event and the array is checked. if it contains only
		// the expected events after the delay, single event is executed. The array is flushed if
		// unexpected events happen. In case you add new expected events, the check logic has to be
		// updated too!
		// We need to do that, because when you click on polyline (for exemple), two events are fired :
		// click on the map and click on the polyline
		clickEvents = [];
	}

	function toggleLowEmissionZoneLayer(isLowEmissionZoneLayerVisible: boolean) {
		const lowEmissionZoneLayersIds = ['Low_Emission_Zone', 'Low_Emission_Zone_Names'];
		lowEmissionZoneLayersIds.forEach((layerId) => {
			loadedMap?.getLayer(layerId)
				&& loadedMap?.setLayoutProperty(
					layerId,
					'visibility',
					isLowEmissionZoneLayerVisible ? 'visible' : 'none',
				);
		});
	}
	function toggleTrafficInfo(isTrafficInfoVisible: boolean) {
		loadedMap?.getStyle().layers?.forEach((layer) => {
			if (layer.id.includes('Traffic')) {
				loadedMap?.setLayoutProperty(
					layer.id,
					'visibility',
					isTrafficInfoVisible ? 'visible' : 'none',
				);
			}
		});
	}

	function handleZoiClick(zoi: Zoi, zoiPosition: number) {
		if (zoi.isArrivalZoi) {
			// We should not be able to handle arrival zoi from the map
			return;
		}

		track({
			action: 'zone_of_interest',
			payload: buildZoiSelectionTrackingData({
				context: 'along_itinerary',
				zoi,
				isArrivalZoi: false,
				zoiCount: zois.length,
				zoiPosition,
				tripOptions: $tripOptionsStore,
			}),
		});

		gotoZoi($searchParams, zoi, $tripOptionsStore.isElectricVehicle);
	}
	const dispatcherEvent: Record<
		Exclude<MobileTopButton, 'search' | 'fit-polylines' | 'back-home' | 'pois-hrt'>,
		(() => void) | undefined
	> = {
		'back-overview': onBackOverview,
		'back-route-results': onBackToRouteResults,
		'back-on-the-way': onBackOnTheWay,
	};

	function getPosition(
		shouldDisplaySummary: boolean,
		$isPinMarkerOnMapWithSummary: boolean,
		$clickedPosition: Coordinates | undefined,
		selectedAddressCenter: Coordinates | undefined,
	) {
		if (shouldDisplaySummary && $isPinMarkerOnMapWithSummary && $clickedPosition) {
			return $clickedPosition;
		} else if (!shouldDisplaySummary && selectedAddressCenter) {
			return selectedAddressCenter;
		}
	}

	function handleFullscreenSwitch() {
		trackDisableFullscreen();

		$isMapFullscreen = false;
	}

	function updateAdVisibility(isSummaryOpen: boolean, currentRoute: { id: string | null }) {
		return (
			!isSummaryOpen
			&& (isMapPageContext(currentRoute)
				|| (isRouteResultsPage(currentRoute) && page.data.locale === 'fr-FR'))
		);
	}

	const sources = new Set<string>(['MTP', 'relief', 'static_modern']);

	function handleSourceData(event: MapSourceDataEvent) {
		const { sourceId, isSourceLoaded } = event;
		const isSourceToBeHandled = sources.has(sourceId);

		if (!event.sourceId || !isSourceToBeHandled) return;

		const measureName = `load-time-${sourceId}`;
		const startMarkName = `start-${sourceId}`;
		const finishMarkName = `finish-${sourceId}`;

		if (!isSourceLoaded && !performance.getEntriesByName(startMarkName).length) {
			performance.mark(startMarkName);
		}

		if (isSourceLoaded && !performance.getEntriesByName(finishMarkName).length) {
			performance.mark(finishMarkName);
			performance.measure(measureName, startMarkName, finishMarkName);

			if (sourceId === 'MTP' && loadedMap?.getLayer('static_modern')) {
				loadedMap.setLayoutProperty('static_modern', 'visibility', 'none');
			}
		}
	}

	function clearPerformanceMarksAndMeasures() {
		sources.forEach((sourceId) => {
			performance.clearMarks(`start-${sourceId}`);
			performance.clearMarks(`finish-${sourceId}`);
			performance.clearMeasures(`load-time-${sourceId}`);
		});
	}

	$effect(() => {
		if ($itineraryResultsStore?.fetching) {
			$partnerBoxMapsAreReduced = false;
		}
	});

	const waypoints = $derived($itineraryResultsStore?.waypoints);
	const geometries = $derived(routes?.map(({ geometry, index }) => ({ line: geometry, index })));

	$effect(() => {
		if ($poiCoordinates) {
			centerMapOnCoordinates($poiCoordinates);
		}
	});

	const defaultZoom = $derived(isMapFlying ? undefined : loadedMap?.getZoom());

	watch(() => getSelectedAddress(), (selectedAddress) => {
		if (selectedAddress) {
			handleSelectedAddressChange(selectedAddress);
		}
	});

	$effect(() => {
		toggleLowEmissionZoneLayer($isLowEmissionZoneLayerVisible);
	});

	$effect(() => {
		toggleTrafficInfo($isTrafficInfoVisible);
	});

	const position = $derived(getPosition(
		shouldDisplaySummary,
		$isPinMarkerOnMapWithSummary,
		$clickedPosition,
		selectedAddressCenter,
	));

	const shouldShowAd = $derived(updateAdVisibility($isSummaryOpen, page.route));
</script>

<MapLibre
	attributionControl={false}
	class="h-full w-full md:shadow-smooth-block {classes}"
	style={$mapStyleUrl}
	bounds={initialMapBounds}
	maxZoom={MAX_ZOOM}
	minZoom={MIN_ZOOM}
	onload={(map) => {
		if (getRTLTextPluginStatus() === 'unavailable') {
			setRTLTextPlugin(MAPLIBRE_RTL_TEXT_PLUGIN_URL, true);
		}

		map.on('click', onMapClick);
		map.on('moveend', onMapMoveEnd);

		$mapController.center = center;
		$mapController.fitCoordinates = fitCoordinates;
		$mapController.fitCircle = fitCircle;
		$mapController.getViewPortBoundsFromCenter = (center, zoom) =>
			getViewPortBoundsFromCenter({
				currentBounds: map.getBounds(),
				currentZoom: map.getZoom(),
				desiredCenter: center,
				desiredZoom: zoom,
			});
		$mapController.getBounds = () => map.getBounds();

		toggleTrafficInfo($isTrafficInfoVisible);
		toggleLowEmissionZoneLayer($isLowEmissionZoneLayerVisible);

		// Disable the default maplibre resizing feature
		// The default behavior adds a throttle that resizes the map every 50 ms
		// This behavior creates a flcikering effect that we want to avoid
		// Instead, we have our own resizing system that is based on a debounce.
		map._trackResize = false;
		const debouncedResize = debounce(30, resize);
		resizeObserver = new ResizeObserver(() => {
			debouncedResize();
		});

		resizeObserver.observe(map.getContainer());
		dispatchMapBoundsChange();
		$isMapLoaded = true;
	}}
	onstyledata={(event) => {
		loadedMap = event.target;

		if ($mapLayerName === 'michelin') {
			loadedMap.on('sourcedata', handleSourceData);
		} else {
			clearPerformanceMarksAndMeasures();
			loadedMap.off('sourcedata', handleSourceData);
		}
		toggleLowEmissionZoneLayer($isLowEmissionZoneLayerVisible);
		toggleTrafficInfo($isTrafficInfoVisible);
	}}
>
	{#if $isMapFullscreen}
		<div class="absolute top-2 left-2 w-90 z-ontop hidden md:block">
			<button
				class=" bg-transparent border-unset p-0 block w-full cursor-pointer"
				onclick={preventDefault(handleFullscreenSwitch)}
			>
				<Header
					variant="map-view"
					rightButton={{
						icon: 'double-chevron-right',
						text: m.action__open_panel(),
						iconPosition: 'right',
						variant: 'ghost',
						color: 'primary',
					}}
					onRightButtonClick={handleFullscreenSwitch}
				/>
			</button>
		</div>
	{/if}
	<div class="absolute bottom-0 right-0 w-full mb-6 pointer-events-none z-ontop">
		<div class="flex flex-col gap-y-4">
			<div class="flex justify-end">
				{#if !$isSummaryOpen && $drawerScrollPosition === 0}
					<div class="mr-2 hidden md:inline-block pointer-events-auto">
						<PartnerBoxMaps />
					</div>
				{/if}
			</div>
			{#if shouldShowAd && mapWidth}
				<div class="flex justify-center mb-1">
					<div class="pointer-events-auto">
						<MapAdBanner {mapWidth} />
					</div>
				</div>
			{/if}
		</div>
	</div>

	{#if routes && routes.length > 0}
		<!--
			Key blocks destroy and recreate their contents when the value of an expression changes.
			When used around components, this will cause them to be reinstantiated and reinitialised.
			https://svelte.dev/docs/logic-blocks#key
		-->
		{#key selectedRoute}
			{@const shouldDisplayOneRoutePolylines = isRouteDetailsPageContext(page.route) || $isOneRoutePolylinesVisible}
			<RoutePolylines
				{geometries}
				{selectedRoute}
				displayOnlySelected={shouldDisplayOneRoutePolylines}
				onPolylineClick={handlePolylineClick}
			/>
			<RouteTrafficPolylines trafficGeometries={routes?.at(selectedRoute)?.trafficMentions ?? []} />

			{#if !shouldDisplayOneRoutePolylines}
				<RouteMarker {selectedRoute} {routes} onclick={handleRouteIndexChange} />
			{/if}

			{#if selectedRouteRechargePoints}
				{#each selectedRouteRechargePoints as rechargePoint (JSON.stringify(rechargePoint.coordinates))}
					<RechargePointMarker coordinates={rechargePoint.coordinates} />
				{/each}
			{/if}
			<ItineraryMarkers {routes} />
		{/key}
	{/if}

	{#if waypoints && waypoints.length > 0}
		{#each waypoints as waypoint (waypoint.name)}
			<WaypointMarker routesWaypoint={waypoint} />
		{/each}
	{/if}

	{#if poiData.length !== 0}
		{#each poiData as poi (poi.id)}
			{#if poi.id === selectedPoiData?.id}
				<LocationMarker coordinates={poi.coordinates} class="z-float" />
			{:else}
				<PoiMarker {poi} detailedView={$poiMarkerWithDetailsEnabled} onclick={handlePoiClick} />
			{/if}
		{/each}
	{/if}

	{#if trafficEvents.length && $isTrafficInfoPageSwitchOn}
		{#each trafficEvents as trafficEvent (JSON.stringify(trafficEvent.coordinates))}
			<TrafficMarker {trafficEvent} onclick={handleTrafficEventClick} />
		{/each}
	{/if}

	{#if zois.length && (isZoiPageContext(page.route) || $isOneRoutePolylinesVisible)}
		{#each zois as zoi, index (zoi.zoiId)}
			<ZoiPolygon
				{zoi}
				fillPolygon={!isZoiPageContext(page.route)}
				onclick={_zoi => handleZoiClick(_zoi, index + 1)}
			/>
		{/each}
	{/if}

	{#if corridor}
		<CorridorPolygon coordinates={corridor} />
	{/if}

	<LocationMarker coordinates={position} />

	<ItineraryMarkers {routes} />
	{#each mobileTopButtons ?? [] as mobileTopButton (mobileTopButton)}
		{#if mobileTopButton === 'search' && !autoReloadPOIs && $selectedPoiCategories.length > 0 && !isMapFlying && mapBounds}
			<RefreshPoisButton
				{mapBounds}
				type={mobileTopButton}
				onRefreshBounds={mapBounds =>
					onSearchBoundsChange?.({
						mapBounds,
						userAction: 'click',
					})}
			/>
		{/if}

		{#if mobileTopButton === 'back-home'}
			<Button
				class="md:hidden absolute top-2 left-2 z-infront"
				icon="west"
				text={m.action__back_home()}
				color="white"
				hideTextOnMobile
				onclick={() => onBackHome?.()}
			/>
		{/if}

		{#if mobileTopButton === 'back-overview' || mobileTopButton === 'back-route-results' || mobileTopButton === 'back-on-the-way'}
			<Button
				class="md:hidden absolute top-2 left-2 z-infront"
				icon="west"
				color="white"
				onclick={() => {
					if (mobileTopButton in dispatcherEvent) {
						dispatcherEvent[mobileTopButton]?.();
					}
				}}
			/>
		{/if}

		{#if mobileTopButton === 'fit-polylines' && currentZoom && defaultZoom && currentZoom - defaultZoom >= 3}
			<Button
				class="md:hidden absolute top-2 left-2 z-infront"
				icon="west"
				text={$isRefreshButtonVisible ? '' : m.action__back_overview()}
				color="white"
				onclick={() =>
					fitCoordinates(
						$itineraryResultsStore?.routes.flatMap(route => route.geometry.coordinates),
					)}
			/>
		{/if}

		{#if mobileTopButton === 'pois-hrt' && currentZoom && currentZoom >= 9 && !$selectedPoiCategories.length && mapBounds}
			<RefreshPoisButton
				{mapBounds}
				type={mobileTopButton}
				onRefreshBounds={mapBounds =>
					onSearchBoundsChange?.({
						mapBounds,
						userAction: 'click',
						selectedPois: POIS_HRT,
					})}
			/>
		{/if}
	{/each}

	<MapTools
		{isTrafficPage}
		selectedLayerName={$mapLayerName}
		selectedPoiCategories={$selectedPoiCategories}
		withPartner={enableToolsPartnerPoiCategory}
		showPoiCategorySelector={!isCorridorPageContext(page.route)}
		onPoiCategoriesChange={categories => onPoiCategoriesChange?.(categories)}
		onGetLocation={() => {
			shouldReloadPoisOnGeolocationGranted = true;
		}}
	/>
	<AttributionControl compact={false} />
	<ScaleControl />
</MapLibre>

<style>
	@media screen and (max-width: 960px) {
		:global(.maplibregl-ctrl-bottom-left) {
			margin-bottom: 38px;
		}
	}
</style>
